import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, Folder, MapPin, Building2, Compass, Sparkles, Trash2, RefreshCw } from "lucide-react";

const EMPTY = {
  contact_email: "", contact_phone: "", contact_address: "", contact_hours: "",
  facebook: "", instagram: "", linkedin: "",
  legal_company: "", legal_siret: "", legal_vat: "", legal_address: "", legal_publisher: "", tagline: "",
  priority_territory: "chartres",
};

export default function AdminSettings() {
  const [f, setF] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [poles, setPoles] = useState([]);
  const [demo, setDemo] = useState(null);
  const [demoBusy, setDemoBusy] = useState(false);

  const loadDemo = () => api.get("/admin/demo/status").then((r) => setDemo(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/admin/site-settings").then((r) => setF({ ...EMPTY, ...r.data }));
    api.get("/public/territory-config").then((r) => setPoles(r.data?.available || [])).catch(() => {});
    loadDemo();
  }, []);

  const runDemo = async (action) => {
    setDemoBusy(true);
    try {
      const { data } = await api.post(`/admin/demo/${action}`);
      if (action === "seed") toast.success(`Démos générées : ${data.partners} partenaires, ${data.offers} offres, ${data.annonces} annonces, ${data.neighbors} Entre Voisins`);
      else toast.success(`${data.deleted} contenus démo supprimés`);
      await loadDemo();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action impossible"); }
    finally { setDemoBusy(false); }
  };

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target?.value ?? e }));
  const save = async () => {
    setLoading(true);
    try {
      const { id, updated_at, ...payload } = f; // eslint-disable-line
      await api.put("/admin/site-settings", payload);
      toast.success("Paramètres enregistrés");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold" data-testid="admin-settings-title">Paramètres</h1>
          <p className="text-slate-500 text-xs md:text-sm mt-1">Coordonnées publiques, réseaux sociaux, mentions légales. Ces informations peuvent être affichées sur le site public.</p>
        </div>
        <Button disabled={loading} onClick={save} className="btn-shine text-white" data-testid="settings-save">
          <Save size={14} className="mr-1"/> {loading ? "…" : "Enregistrer"}
        </Button>
      </div>

      <div className="mt-6 grid md:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3">
          <div className="font-heading font-bold text-lg flex items-center gap-2"><Building2 size={16}/> Coordonnées publiques</div>
          <div><Label>Email de contact</Label><Input value={f.contact_email} onChange={set("contact_email")} data-testid="settings-email"/></div>
          <div><Label>Téléphone</Label><Input value={f.contact_phone} onChange={set("contact_phone")} data-testid="settings-phone"/></div>
          <div><Label>Adresse postale</Label><Input value={f.contact_address} onChange={set("contact_address")} data-testid="settings-address"/></div>
          <div><Label>Horaires</Label><Input value={f.contact_hours} onChange={set("contact_hours")} placeholder="Lun–Ven 9h–17h" data-testid="settings-hours"/></div>
          <div><Label>Slogan / accroche</Label><Textarea rows={2} value={f.tagline} onChange={set("tagline")} data-testid="settings-tagline"/></div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3">
          <div className="font-heading font-bold text-lg">Réseaux sociaux</div>
          <div><Label>Facebook</Label><Input value={f.facebook} onChange={set("facebook")} placeholder="https://facebook.com/…" data-testid="settings-fb"/></div>
          <div><Label>Instagram</Label><Input value={f.instagram} onChange={set("instagram")} placeholder="https://instagram.com/…" data-testid="settings-ig"/></div>
          <div><Label>LinkedIn</Label><Input value={f.linkedin} onChange={set("linkedin")} placeholder="https://linkedin.com/…" data-testid="settings-in"/></div>

          <div className="font-heading font-bold text-lg pt-3 border-t border-slate-100">Mentions légales</div>
          <div><Label>Raison sociale</Label><Input value={f.legal_company} onChange={set("legal_company")} data-testid="settings-legal-company"/></div>
          <div><Label>SIRET</Label><Input value={f.legal_siret} onChange={set("legal_siret")} data-testid="settings-siret"/></div>
          <div><Label>N° TVA intracommunautaire</Label><Input value={f.legal_vat} onChange={set("legal_vat")} placeholder="FR..." data-testid="settings-vat"/></div>
          <div><Label>Adresse de facturation</Label><Input value={f.legal_address} onChange={set("legal_address")} placeholder="N°, rue, CP, ville" data-testid="settings-legal-address"/></div>
          <div><Label>Directeur de la publication</Label><Input value={f.legal_publisher} onChange={set("legal_publisher")} data-testid="settings-publisher"/></div>
        </div>
      </div>

      {/* Territoire prioritaire — pilote la hiérarchie de la Home */}
      <div className="mt-6 bg-white border border-emerald-200 rounded-2xl p-5" data-testid="settings-territory-section">
        <div className="font-heading font-bold text-lg flex items-center gap-2"><Compass size={16} className="text-emerald-700"/> Territoire prioritaire</div>
        <p className="text-xs text-slate-500 mt-1">
          Ce territoire est mis en avant sur la page d'accueil (section « Les bons plans près de chez vous »). Les autres pôles restent accessibles sous « Découvrez également les bons plans partout en Eure-et-Loir ».
          <br/>Modifier cette valeur pour faire évoluer la stratégie du site sans refonte.
        </p>
        <div className="mt-3 max-w-md">
          <Label>Pôle mis en avant sur la Home</Label>
          <Select value={f.priority_territory || "chartres"}
                  onValueChange={(v) => setF((s) => ({ ...s, priority_territory: v }))}>
            <SelectTrigger data-testid="settings-priority-territory"><SelectValue placeholder="Choisir un pôle"/></SelectTrigger>
            <SelectContent>
              {poles.map((p) => (
                <SelectItem key={p.id} value={p.id} data-testid={`settings-priority-opt-${p.id}`}>
                  {p.name} — centre : {p.center}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-6 bg-white border border-amber-200 rounded-2xl p-5" data-testid="settings-demo-section">
        <div className="font-heading font-bold text-lg flex items-center gap-2"><Sparkles size={16} className="text-amber-600"/> Données de démonstration</div>
        <p className="text-xs text-slate-500 mt-1">
          Remplit les espaces (partenaires, offres, petites annonces, Entre Voisins) avec des exemples fictifs marqués « démo ».
          À générer <b>sur chaque environnement</b> (l'aperçu et la production ont des bases séparées) : après publication, revenez ici et cliquez sur « Générer ».
        </p>
        <div className="mt-2 text-sm text-slate-600" data-testid="settings-demo-count">
          {demo ? (demo.total > 0
            ? `Actuellement : ${demo.total} contenus démo (${demo.by_collection.businesses} partenaires, ${demo.by_collection.offers} offres, ${demo.by_collection.annonces} annonces, ${demo.by_collection.neighbor_posts} Entre Voisins).`
            : "Aucun contenu démo dans cet environnement.") : "…"}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button disabled={demoBusy} onClick={() => runDemo("seed")} className="btn-shine text-white" data-testid="demo-seed-btn">
            <RefreshCw size={14} className="mr-1"/> {demoBusy ? "…" : (demo && demo.total > 0 ? "Régénérer les démos" : "Générer les démos")}
          </Button>
          {demo && demo.total > 0 && (
            <Button disabled={demoBusy} variant="outline" onClick={() => runDemo("purge")} className="border-rose-300 text-rose-600 hover:bg-rose-50" data-testid="demo-purge-btn">
              <Trash2 size={14} className="mr-1"/> Supprimer les démos
            </Button>
          )}
        </div>
      </div>

      <div className="mt-6 bg-slate-50 border border-slate-200 rounded-2xl p-5">
        <div className="font-heading font-bold text-lg mb-3">Référentiels</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Link to="/admin/categories" className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-xl hover:border-[#E05638] transition" data-testid="settings-link-cats">
            <Folder size={18} className="text-[#E05638]"/>
            <div>
              <div className="font-semibold">Catégories</div>
              <div className="text-xs text-slate-500">Gérer la liste des catégories affichées sur le site.</div>
            </div>
          </Link>
          <Link to="/admin/villes" className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-xl hover:border-[#E05638] transition" data-testid="settings-link-cities">
            <MapPin size={18} className="text-[#E05638]"/>
            <div>
              <div className="font-semibold">Villes</div>
              <div className="text-xs text-slate-500">Villes couvertes en Eure-et-Loir.</div>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}
